import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Video, Lock, Users, CalendarDays, CheckSquare } from 'lucide-react';
import { MemberChips } from './TeamMeetingDialog';
import { DeleteEventButton } from './DeleteEventButton';

export const CEO_TYPE_CFG = {
  reunion_cliente: { icon: Users, dot: 'bg-blue-500', text: 'text-blue-600', block: 'bg-blue-100 text-blue-950 border-blue-500', label: 'Reunión con cliente' },
  shooting: { icon: Video, dot: 'bg-purple-500', text: 'text-purple-600', block: 'bg-purple-100 text-purple-950 border-purple-500', label: 'Shooting / grabación' },
  evento: { icon: CalendarDays, dot: 'bg-[#f56258]', text: 'text-[#f56258]', block: 'bg-red-100 text-red-950 border-[#f56258]', label: 'Evento' },
  tarea: { icon: CheckSquare, dot: 'bg-emerald-500', text: 'text-emerald-600', block: 'bg-emerald-100 text-emerald-950 border-emerald-500', label: 'Tarea', isTask: true },
  ausencia: { icon: CalendarDays, dot: 'bg-rose-500', text: 'text-rose-600', block: 'bg-rose-100 text-rose-950 border-rose-500', label: 'Vacaciones / ausencia' },
  personal: { icon: Lock, dot: 'bg-amber-500', text: 'text-amber-600', block: 'bg-amber-100 text-amber-950 border-amber-500', label: 'Personal' },
};

export const emptyCeoForm = (fecha, today) => ({
  id: null, kind: 'event', type: 'evento', titulo: '', fecha: fecha || today, fecha_fin: '', all_day: false,
  hora_inicio: '', hora_fin: '', meet_link: '', generar_meet: false, descripcion: '', client_id: '',
  privado: false, invitados: [], invitar_todos: false, completada: false,
});

export const CeoEventDialog = ({ open, onOpenChange, form, setForm, members, clients, googleConnected, onSave, saving, onDelete, deleting, onTeamShare, sharing }) => {
  const set = (patch) => setForm({ ...form, ...patch });
  const isTask = form.kind === 'task';
  const needsClient = form.type === 'reunion_cliente' || form.type === 'shooting';
  const multiDay = form.type === 'ausencia' || !!form.fecha_fin;
  const toggleInv = (id) => set({ invitados: form.invitados.includes(id) ? form.invitados.filter((x) => x !== id) : [...form.invitados, id], invitar_todos: false });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ceo-event-dialog">
        <DialogHeader><DialogTitle>{form.id ? (isTask ? 'Editar tarea' : 'Editar evento') : 'Nuevo en mi calendario'}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Formulario del calendario CEO</DialogDescription>
        <div className="py-1 space-y-3">
          <div className="flex items-center rounded-lg border p-0.5 w-fit" data-testid="kind-switch">
            {[['event', 'Evento'], ['task', 'Tarea']].map(([k, lbl]) => (
              <button key={k} type="button" onClick={() => set({ kind: k, type: k === 'task' ? 'tarea' : (form.type === 'tarea' ? 'evento' : form.type) })}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${form.kind === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`kind-${k}-btn`}>{lbl}</button>
            ))}
          </div>
          {!isTask && (
            <div>
              <Label>Tipo</Label>
              <select value={form.type} onChange={(e) => set({ type: e.target.value, all_day: e.target.value === 'ausencia' ? true : form.all_day, privado: e.target.value === 'personal' ? true : form.privado, fecha_fin: e.target.value === 'ausencia' && !form.fecha_fin ? form.fecha : form.fecha_fin })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-background h-10" data-testid="ceo-type-select">
                {Object.entries(CEO_TYPE_CFG).filter(([k]) => k !== 'tarea').map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label>Título</Label>
            <Input value={form.titulo} onChange={(e) => set({ titulo: e.target.value })} placeholder={isTask ? 'Ej: Preparar propuesta' : 'Ej: Reunión semanal del equipo'} data-testid="ceo-title-input" />
          </div>
          {needsClient && (
            <div>
              <Label>Cliente</Label>
              <select value={form.client_id} onChange={(e) => set({ client_id: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm bg-background h-10" data-testid="ceo-client-select">
                <option value="">— Sin cliente —</option>
                {clients.map((c) => <option key={c._id || c.id} value={c._id || c.id}>{c.nombre || c.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{multiDay ? 'Desde' : 'Fecha'}</Label>
              <Input type="date" value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} data-testid="ceo-date-input" />
            </div>
            {multiDay ? (
              <div>
                <Label>Hasta</Label>
                <Input type="date" value={form.fecha_fin} min={form.fecha} onChange={(e) => set({ fecha_fin: e.target.value })} data-testid="ceo-date-end-input" />
              </div>
            ) : (
              <div className="flex items-end pb-2">
                <button type="button" className="text-xs text-primary hover:underline" onClick={() => set({ fecha_fin: form.fecha })} data-testid="ceo-multiday-btn">Varios días…</button>
              </div>
            )}
          </div>
          {!isTask && form.type !== 'ausencia' && (
            <div className="flex items-center gap-3">
              <Switch checked={form.all_day} onCheckedChange={(v) => set({ all_day: v })} data-testid="ceo-allday-switch" />
              <Label>Todo el día</Label>
            </div>
          )}
          {!isTask && !form.all_day && form.type !== 'ausencia' && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Hora inicio</Label><Input type="time" value={form.hora_inicio} onChange={(e) => set({ hora_inicio: e.target.value })} data-testid="ceo-start-input" /></div>
              <div><Label>Hora fin</Label><Input type="time" value={form.hora_fin} onChange={(e) => set({ hora_fin: e.target.value })} data-testid="ceo-end-input" /></div>
            </div>
          )}
          {!isTask && form.type !== 'ausencia' && (
            <div>
              <Label className="flex items-center gap-1"><Video className="w-3.5 h-3.5" /> Enlace de Meet</Label>
              <Input value={form.meet_link} onChange={(e) => set({ meet_link: e.target.value })} placeholder="https://meet.google.com/..." data-testid="ceo-meet-input" />
              {googleConnected && !form.meet_link && (
                <label className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={form.generar_meet} onChange={(e) => set({ generar_meet: e.target.checked })} data-testid="ceo-generate-meet-check" />
                  Generar enlace de Meet automáticamente con Google
                </label>
              )}
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-muted-foreground" /><div><p className="text-sm font-medium">Privado</p><p className="text-xs text-muted-foreground">Solo lo ves tú (ni la otra CEO ni el equipo)</p></div></div>
            <Switch checked={form.privado} onCheckedChange={(v) => set({ privado: v, invitados: v ? [] : form.invitados, invitar_todos: v ? false : form.invitar_todos })} data-testid="ceo-private-switch" />
          </div>
          {!form.privado && !isTask && form.type !== 'ausencia' && (
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" /><p className="text-sm font-medium">Invitar al equipo</p></div>
                {!form.gcal_series_id && (
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={form.invitar_todos} onChange={(e) => set({ invitar_todos: e.target.checked, invitados: e.target.checked ? [] : form.invitados })} data-testid="ceo-invite-all-check" />
                    Todo el equipo
                  </label>
                )}
              </div>
              {form.id && form.gcal_series_id && onTeamShare && (
                <div className="flex items-center justify-between rounded-md bg-muted/50 p-2" data-testid="team-share-block">
                  <div>
                    <p className="text-sm font-medium">Para todo el equipo · aplicar a todas las repeticiones</p>
                    <p className="text-[11px] text-muted-foreground">Viene de Google. Lo verán los cinco en Eventos con su enlace, también las repeticiones nuevas. Se aplica al momento.</p>
                  </div>
                  <Switch checked={form.invitar_todos} disabled={sharing} onCheckedChange={onTeamShare} data-testid="team-share-switch" />
                </div>
              )}
              {!form.invitar_todos && <MemberChips members={members} selected={form.invitados} onToggle={toggleInv} testPrefix="ceo-invite" />}
              <p className="text-[11px] text-muted-foreground">Solo las personas invitadas verán este evento (con su enlace de Meet) en su calendario.</p>
            </div>
          )}
          {isTask && (
            <div className="flex items-center gap-3">
              <Switch checked={form.completada} onCheckedChange={(v) => set({ completada: v })} data-testid="ceo-done-switch" />
              <Label>Completada</Label>
            </div>
          )}
          <div>
            <Label>Descripción (opcional)</Label>
            <Textarea value={form.descripcion} onChange={(e) => set({ descripcion: e.target.value })} rows={2} placeholder="Detalles..." data-testid="ceo-desc-input" />
          </div>
        </div>
        <DialogFooter className="sm:justify-between gap-2">
          {form.id && onDelete && (
            <DeleteEventButton title={form.titulo} deleting={deleting} onConfirm={() => onDelete(form)} testId="delete-ceo-event-btn"
              description={`${isTask ? 'La tarea' : 'El evento'} desaparecerá de tu calendario${form.gcal_event_id ? ' y de Google Calendar' : ''}${(form.invitar_todos || form.invitados?.length) ? ', y las personas invitadas recibirán aviso de la cancelación' : ''}. Podrás deshacerlo durante unos minutos.`} />
          )}
          <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={onSave} disabled={saving || !form.titulo.trim() || !form.fecha} data-testid="save-ceo-event-btn">
            {saving ? 'Guardando...' : (form.id ? 'Guardar' : 'Añadir')}
          </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
